import { Installment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Stack,
} from '@mui/material';

import Button from '../../../components/styledComponents/Button';
import { RemoveButton } from '../../../components/styledComponents/IconButtons';
import colors from '../../../constants/colors';

interface RowProps {
  text: string;
  fee: string;
}

const DarkRow = ({ text, fee }: RowProps) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    sx={{
      px: 2,
      py: 1,
      boxShadow: 'inset 0px 1px 0px #E7E8E9',
      backgroundColor: colors.csaaNeutralBackground,
    }}
  >
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: colors.csaaBlack,
        flex: 9,
      }}
    >
      {text}
    </Typography>
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 700,
        color: colors.csaaBlack,
        flex: 1,
        textAlign: 'left',
      }}
    >
      {fee}
    </Typography>
  </Stack>
);

interface LightRowProps extends RowProps {
  last?: boolean;
}

const LightRow = ({ text, fee, last = false }: LightRowProps) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    sx={{
      px: 2,
      py: 1,
      boxShadow: last
        ? '0px 1px 0px #E7E8E9, inset 0px 1px 0px #E7E8E9'
        : 'inset 0px 1px 0px #E7E8E9',
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 400,
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        color: colors.csaaLightBorder,
        flex: 9,
      }}
    >
      {text}
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 400,
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        color: colors.csaaLightBorder,
        textAlign: 'left',
      }}
    >
      {fee}
    </Typography>
  </Stack>
);

interface InstallmentFeesDialogProps {
  closeDialog: () => void;
  open: boolean;
  installmentFees?: Installment[];
}

const InstallmentFeesDialog = ({
  closeDialog,
  open,
  installmentFees,
}: InstallmentFeesDialogProps) => {
  const autoPayInstallment = installmentFees?.find(
    (fee) => fee.installmentType === 'AUTOPAY'
  );
  const directInstallment = installmentFees?.find(
    (fee) => fee.installmentType === 'DIRECT'
  );

  return (
    <MUIDialog
      aria-labelledby="installmentFeesLabel"
      data-testid="installmentFeesDialog"
      open={open}
      onClose={closeDialog}
      sx={{ backdropFilter: 'blur(5px)' }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: 1,
          mt: { mobile: 2, tablet: 3 },
        }}
      >
        <RemoveButton
          aria-label="Close installment fees dialog"
          data-testid="closeIconInstallmentFeesDialogButton"
          onClick={closeDialog}
          autoFocus
        />
      </Box>
      <DialogTitle
        id="installmentFeesLabel"
        sx={{
          fontSize: 28,
          fontWeight: 700,
          textAlign: 'center',
          color: colors.csaaBlack,
          p: 0,
          my: 3,
        }}
      >
        Installment fees
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 3,
          pb: 6,
          px: 0,
        }}
      >
        <Box sx={{ pb: 7 }}>
          <DarkRow text="AutoPay method" fee="Fee" />
          {autoPayInstallment?.fees &&
            autoPayInstallment.fees.map((fee) => (
              <LightRow
                key={fee.code}
                text={fee.description}
                fee={fee.amount}
              />
            ))}
          <DarkRow text="One-time payment method" fee="Fee" />
          {directInstallment?.fees &&
            directInstallment.fees.map((fee) => (
              <LightRow
                key={fee.code}
                text={fee.description}
                fee={fee.amount}
              />
            ))}
        </Box>
        <Button
          fullWidth
          data-testid="closeInstallmentFeesDialogButton"
          variant="contained"
          sx={{
            whiteSpace: 'nowrap',
            height: { mobile: 36, tablet: 42 },
          }}
          onClick={closeDialog}
        >
          Close
        </Button>
      </DialogContent>
    </MUIDialog>
  );
};

export default InstallmentFeesDialog;
